import * as R from 'ramda';
import { Match } from '../services/faceAuthentication/facetec/facetecProcessor';
import { NetworkConfig, icons, networkConfigs } from '../common/types';

export function moveItemToFront<T>(array: T[], itemToMove: T): T[] {
  if (R.includes(itemToMove, array)) {
    const newArray = array.filter((value) => value !== itemToMove);
    newArray.unshift(itemToMove);
    return newArray;
  }
  return array;
}

export function getNetworkByChainId(
  chainId: number,
  configs: Record<string, NetworkConfig>,
): NetworkConfig | undefined {
  // eslint-disable-next-line no-restricted-syntax
  for (const name in configs) {
    if (configs[name].chainId === chainId) {
      return configs[name];
    }
  }

  return undefined; // Return undefined if no matching chainId is found
}

export function getNetworkNameByChainId(
  chainNetwork: string,
  configs: Record<string, NetworkConfig>,
): string | undefined {
  const chainId = parseInt(chainNetwork, 10);
  // eslint-disable-next-line no-restricted-syntax
  for (const name in configs) {
    if (configs[name].chainId === chainId) {
      return name;
    }
  }
  return undefined; // return undefined if no matching chainId is found
}

// Check if the chain is a mainnet. Default to true.
export function isChainNetworkMainnet(
  chain: string,
  chainNetwork: string,
  configs: Record<string, NetworkConfig>,
): boolean {
  // If we don't have a chain or chain network, like during revocation flow, assume mainnet
  if (R.isNil(chain) || R.isNil(chainNetwork)) return true;

  if (chain.toLowerCase() === 'ethereum') {
    const networkConfig = getNetworkByChainId(parseInt(chainNetwork, 10), configs);
    if (networkConfig) {
      return networkConfig.isMainnet ?? true;
    }
    return true;
  }

  if (chain.toLowerCase() === 'solana') {
    return !!chainNetwork && R.includes('mainnet', chainNetwork.toLowerCase());
  }

  if (chain.toLowerCase() === 'casper') {
    return !!chainNetwork && R.includes('casper', chainNetwork.toLowerCase());
  }

  // Default to true
  return true;
}

export function getNetworkByNetworkName(
  network: string,
  configs: Record<string, NetworkConfig>,
): NetworkConfig | undefined {
  // eslint-disable-next-line no-restricted-syntax
  return configs[network];
}

const renameKeys = R.curry((keysMap, obj) =>
  R.reduce((acc, key) => R.assoc(keysMap[key] || key, obj[key], acc), {}, R.keys(obj)),
);

export function renameObjectKeys(
  source: Record<string, string>,
  mapping: Record<string, string>,
): Record<string, string> {
  return renameKeys(mapping)(source);
}

export const addressFromDid = (did: string): string => {
  const regExpMatchArray = did.match(/:([^:]+$)/);
  if (!regExpMatchArray) throw new Error('Invalid DID');
  return regExpMatchArray[1];
};

// truncates a string
export const truncate = (str: string, n: number): string => {
  return str.length > n ? `${str.substr(0, n - 1)}...` : str;
};

export const getNetworkName = (network: string): string => {
  // if not nan lookup network name
  // eslint-disable-next-line radix
  const id = parseInt(network);
  if (Number.isNaN(id)) {
    return network;
  }

  return getNetworkByChainId(id, networkConfigs)?.network ?? network;
};

export const getNetworkConfig = (network: string) => {
  const id = parseInt(network, 10);
  return getNetworkByChainId(id, networkConfigs);
};

export const getChainAndIcon = (match: Match) => {
  const { chain, network } = match;
  switch (chain) {
    case 'ethereum': {
      // check if network is a number
      const chainId = parseInt(network, 10);
      const networkInfo = !Number.isNaN(chainId)
        ? getNetworkByChainId(chainId, networkConfigs)
        : getNetworkByNetworkName(network, networkConfigs);

      if (networkInfo) {
        return { chain: networkInfo.network, icon: icons[networkInfo.chainId].icon };
      }

      return { chain: match.network, icon: undefined };
    }
    case 'solana': {
      return { chain: 'Solana', icon: 'solana' };
    }
    default:
      return { chain, icon: undefined };
  }
};

/**
 * Prepare an object to be sent as URL params, which expects a string value for any object keys
 * any null or empty values should be removed
 * @param Record<string, unknown>
 * @returns Record<string, string>
 */
export const objectToURLParams = <R extends Record<string, string>>(obj: Record<string, unknown>): R =>
  Object.entries(obj).reduce((a, [k, v]) => {
    if (
      v === undefined ||
      v === null ||
      (v instanceof Array && v.length === 0) ||
      (v instanceof Object && Object.keys(v).length === 0) ||
      v === ''
    ) {
      return a;
    }
    return { ...a, [k]: v.toString() }; // Convert to string as needed
  }, {} as R);
